/**
 * Entity Gallery Cache Service
 * 
 * Prevents duplicate API calls and rate limit issues by:
 * - Caching gallery data in memory
 * - Deduplicating concurrent requests for same entity
 * - Respecting rate limits with smart throttling
 */

import { entityAPI } from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('GalleryCache')

class EntityGalleryCache {
  constructor() {
    // Cache storage: entityId -> { data, timestamp }
    this.cache = new Map()
    
    // In-flight requests: entityId -> Promise
    this.inflightRequests = new Map()
    
    // Rate limiting: entityId -> lastFetchTime
    this.rateLimitMap = new Map()
    
    // Last log time per entityId to prevent duplicate logs
    this.lastLogMap = new Map()
    
    // Cache duration: 5 minutes
    this.CACHE_TTL = 5 * 60 * 1000
    
    // Rate limit: 1 request per entity per 2 seconds
    this.RATE_LIMIT_WINDOW = 2000
    
    // Log debounce: 100ms
    this.LOG_DEBOUNCE = 100
  }

  /**
   * Get gallery for entity with caching and deduplication
   * @param {string} entityId - Entity UUID
   * @param {boolean} forceFresh - Bypass cache and fetch fresh
   * @returns {Promise<Array>} Gallery images array
   */
  async getGallery(entityId, forceFresh = false) {
    if (!entityId) {
      return []
    }

    // Check if request is already in-flight FIRST (before any other checks)
    // This prevents duplicate requests during concurrent React renders
    if (this.inflightRequests.has(entityId)) {
      logger.debug('Gallery fetch already in progress, awaiting', { entityId })
      return this.inflightRequests.get(entityId)
    }

    // Check cache second (unless forcing fresh)
    if (!forceFresh) {
      const cached = this.getCached(entityId)
      if (cached) {
        // Only log if we haven't logged recently (debounce duplicate logs from React re-renders)
        const lastLog = this.lastLogMap.get(entityId) || 0
        if (Date.now() - lastLog > this.LOG_DEBOUNCE) {
          logger.debug('Gallery cache hit', { entityId })
          this.lastLogMap.set(entityId, Date.now())
        }
        return cached
      }
    }

    // Check rate limit last
    if (!forceFresh && this.isRateLimited(entityId)) {
      logger.debug('Gallery fetch rate limited, returning cached', { entityId })
      // Return cached even if stale, rather than hitting rate limit
      const stale = this.cache.get(entityId)
      return stale?.data || []
    }

    // Make the request
    const requestPromise = this.fetchGallery(entityId)
    this.inflightRequests.set(entityId, requestPromise)

    try {
      const data = await requestPromise
      return data
    } finally {
      // Clean up in-flight tracking
      this.inflightRequests.delete(entityId)
    }
  }

  /**
   * Internal fetch method
   */
  async fetchGallery(entityId) {
    try {
      logger.debug('Fetching gallery from API', { entityId })
      
      // Update rate limit timestamp BEFORE making request
      this.rateLimitMap.set(entityId, Date.now())
      
      const response = await entityAPI.getEntityGallery(entityId)
      
      // Handle both array and object responses
      let images = []
      if (Array.isArray(response.data)) {
        images = response.data
      } else if (response.data && Array.isArray(response.data.images)) {
        images = response.data.images
      }

      // Store in cache
      this.cache.set(entityId, {
        data: images,
        timestamp: Date.now()
      })

      return images
    } catch (error) {
      // If 429, extend rate limit to avoid hammering
      if (error.response?.status === 429) {
        logger.warn('Gallery fetch rate limited by backend', { entityId })
        this.rateLimitMap.set(entityId, Date.now() + 5000) // Extra 5s penalty
      }
      
      logger.error('Failed to fetch gallery', { entityId, error: error.message })
      
      // Return empty array on error
      return []
    }
  }

  /**
   * Get cached data if valid
   */
  getCached(entityId) {
    const cached = this.cache.get(entityId)
    if (!cached) return null

    const age = Date.now() - cached.timestamp
    if (age > this.CACHE_TTL) {
      // Cache expired
      this.cache.delete(entityId)
      return null
    }

    return cached.data
  }

  /**
   * Check if entity is rate limited
   */
  isRateLimited(entityId) {
    const lastFetch = this.rateLimitMap.get(entityId)
    if (!lastFetch) return false

    const timeSince = Date.now() - lastFetch
    return timeSince < this.RATE_LIMIT_WINDOW
  }

  /**
   * Clear cache for specific entity
   */
  invalidate(entityId) {
    this.cache.delete(entityId)
    this.rateLimitMap.delete(entityId)
    logger.debug('Gallery cache invalidated', { entityId })
  }

  /**
   * Clear all cache
   */
  clearAll() {
    this.cache.clear()
    this.rateLimitMap.clear()
    this.inflightRequests.clear()
    logger.debug('Gallery cache cleared')
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      inflightRequests: this.inflightRequests.size,
      rateLimitedEntities: Array.from(this.rateLimitMap.entries())
        .filter(([_, time]) => Date.now() - time < this.RATE_LIMIT_WINDOW)
        .length
    }
  }
}

// Singleton instance
const galleryCache = new EntityGalleryCache()

export default galleryCache
